/**
 * Billing API Service (TASK-062)
 *
 * API client functions for billing history and invoice management.
 */
import apiClient from './client';
import type {
  Payment,
  Invoice,
  PaginatedResponse,
  PaymentFilters,
} from '../../types/billing';

export const billingApi = {
  /**
   * Get paginated list of payments with optional filters
   */
  getPayments: async (
    params?: PaymentFilters
  ): Promise<PaginatedResponse<Payment>> => {
    const response = await apiClient.get<PaginatedResponse<Payment>>(
      '/api/v1/billing/payments/',
      { params }
    );
    return response.data;
  },

  /**
   * KAN-343: один платёж по id — для страницы результата оплаты.
   *
   * Шлюз возвращает человека на один и тот же адрес при любом исходе, поэтому
   * «что случилось» кабинет обязан спросить сам, а не выводить из того, что
   * его вообще вернули.
   */
  getPayment: async (paymentId: string): Promise<Payment> => {
    const response = await apiClient.get<Payment>(
      `/api/v1/billing/payments/${paymentId}/`
    );
    return response.data;
  },

  /**
   * Get list of invoices
   */
  getInvoices: async (): Promise<PaginatedResponse<Invoice>> => {
    const response = await apiClient.get<PaginatedResponse<Invoice>>(
      '/api/v1/billing/invoices/'
    );
    return response.data;
  },

  /**
   * Generate an invoice for a specific payment
   */
  generateInvoice: async (paymentId: string): Promise<Invoice> => {
    const response = await apiClient.post<Invoice>(
      '/api/v1/billing/invoices/generate/',
      { payment_id: paymentId }
    );
    return response.data;
  },

  /**
   * Download an invoice PDF
   */
  downloadInvoice: async (invoiceId: string): Promise<void> => {
    const response = await apiClient.get(
      `/api/v1/billing/invoices/${invoiceId}/download/`,
      { responseType: 'blob' }
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice-${invoiceId}.pdf`;
    link.click();
    window.URL.revokeObjectURL(url);
  },
};

export default billingApi;
